import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import DonutChart from "../../components/charts/DonutChart";
import Layout from "../../components/layout/Layout";
import {
  addPlaylistToScreen,
  screenFlagOff,
  uncheckAssignedScreen,
  updateScreenMemo,
  updateScreenName,
} from "../../Redux/Actions/screenAction";
import { getPlaylist } from "../../Redux/Actions/playlistAction";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import MemoryIcon from "@mui/icons-material/Memory";
import "./setting.css";

function Setting({ user, socketRef }) {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { screens, isUnChecked } = useSelector((state) => state.Screen);
  const { playlist } = useSelector((state) => state.Playlist);

  const filteredIds = JSON.parse(sessionStorage.getItem('screenFilteredIds') || '[]');
  const currentIndex = filteredIds.indexOf(params.id);
  const prevId = currentIndex > 0 ? filteredIds[currentIndex - 1] : null;
  const nextId = currentIndex < filteredIds.length - 1 ? filteredIds[currentIndex + 1] : null;
  const isBulkTarget = filteredIds.length > 1;

  const [playlistInfo, setPlaylistInfo] = useState({});
  const [code, setCode] = useState("");
  const [openModel, setOpenModel] = useState(false);
  const [message, setMessage] = useState("");
  const [duration, setDuration] = useState(60);
  const [memo, setMemo] = useState("");
  const [memoSaved, setMemoSaved] = useState(false);
  const [openPlaylistDialog, setOpenPlaylistDialog] = useState(false);
  const [playlistSearch, setPlaylistSearch] = useState("");
  const [playlistSort, setPlaylistSort] = useState("name-asc");
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [pingResult, setPingResult] = useState(null); // { connected, latency, ts }
  const [pinging, setPinging] = useState(false);
  const [deviceStats, setDeviceStats] = useState(null);  // 실시간 통계 { cpu, ram, storage, updatedAt }
  const [statsLoading, setStatsLoading] = useState(false);
  const [screenNameEdit, setScreenNameEdit] = useState("");
  const [nameSyncWithPlaylist, setNameSyncWithPlaylist] = useState(true);
  const [openNameSyncDialog, setOpenNameSyncDialog] = useState(false);
  const [pendingNameSave, setPendingNameSave] = useState(null);
  const [nameSaving, setNameSaving] = useState(false);

  const nameEndsWithM = (n) => /[-_]m$/i.test((n || "").trim());

  useEffect(() => {
    const s = screens.find((s) => s._id === params.id);
    setPlaylistInfo({ ...s, screenAssigned: false });
    setMemo(s?.memo || "");
    setMemoSaved(false);
    setScreenshot(null);
    setDeviceStats(null);  // 페이지 전환 시 실시간 통계 초기화
    setStatsLoading(false);
    setScreenNameEdit(s?.name || "");
    const syncDefault = s?.nameSyncWithPlaylist !== false && !nameEndsWithM(s?.name);
    setNameSyncWithPlaylist(syncDefault);
  }, [params.id, screens]);

  useEffect(() => {
    if (isUnChecked) {
      setPlaylistInfo({ ...screens.find((s) => s._id === params.id) });
      dispatch(screenFlagOff());
    }
  }, [isUnChecked]);

  // Listen for screenshot from this device
  useEffect(() => {
    if (!socketRef?.current || !playlistInfo?.mac) return;
    const handler = ({ mac, image }) => {
      if (mac === playlistInfo.mac) {
        setScreenshot(image);
        setScreenshotLoading(false);
      }
    };
    socketRef.current.on('screenshot_received', handler);
    return () => socketRef.current?.off?.('screenshot_received', handler);
  }, [playlistInfo?.mac, socketRef]);

  // Listen for ping result
  useEffect(() => {
    if (!socketRef?.current) return;
    const handler = ({ mac, connected, latency }) => {
      if (mac === playlistInfo?.mac) {
        setPingResult({ connected, latency, ts: new Date().toLocaleTimeString() });
        setPinging(false);
      }
    };
    socketRef.current.on('ping_result', handler);
    return () => socketRef.current?.off?.('ping_result', handler);
  }, [playlistInfo?.mac, socketRef]);

  // 디바이스 리소스 통계 실시간 수신
  useEffect(() => {
    if (!socketRef?.current) return;
    const handler = ({ mac, cpu, ram, storage, updatedAt, offline }) => {
      if (mac !== playlistInfo?.mac) return;
      setStatsLoading(false);
      if (offline) return;
      setDeviceStats({ cpu, ram, storage, updatedAt });
    };
    socketRef.current.on('device_stats_update', handler);
    return () => socketRef.current?.off?.('device_stats_update', handler);
  }, [playlistInfo?.mac, socketRef]);

  const handleRequestScreenshot = () => {
    if (!playlistInfo?.mac || !socketRef?.current) return;
    setScreenshotLoading(true);
    socketRef.current.emit('admin_request_screenshot', { mac: playlistInfo.mac });
  };

  const handleClearCache = () => {
    if (!playlistInfo?.mac || !socketRef?.current) return;
    socketRef.current.emit('admin_clear_cache', { mac: playlistInfo.mac });
  };

  const handleRequestStats = () => {
    if (!playlistInfo?.mac || !socketRef?.current) return;
    setStatsLoading(true);
    socketRef.current.emit('admin_request_stats', { mac: playlistInfo.mac });
    // 10초 내 응답 없으면 timeout
    setTimeout(() => setStatsLoading(false), 10000);
  };

  const handlePingDevice = () => {
    if (!playlistInfo?.mac || !socketRef?.current) return;
    setPinging(true);
    setPingResult(null);
    socketRef.current.emit('admin_ping_device', { mac: playlistInfo.mac, timestamp: Date.now() });
    // 5초 내 응답 없으면 timeout
    setTimeout(() => {
      setPinging(prev => {
        if (prev) setPingResult({ connected: false, latency: null, ts: new Date().toLocaleTimeString() });
        return false;
      });
    }, 5000);
  };

  const submit = () => {
    socketRef?.current.emit("message", {
      message,
      duration,
      mac: playlistInfo.mac,
    });
    setOpenModel(false);
    setMessage("");
  };

  const emitAssignPlaylistToScreen = (id) => {
    // backend가 AddPlaylistToScreen 성공 후 직접 device에 notify하므로
    // frontend는 1초 후 backup emit만 수행
    const screen = screens.find((s) => s._id === id);
    if (!screen) return;
    setTimeout(() => {
      socketRef.current?.emit("playlist_added", {
        detail: { mac: screen?.mac, code: id },
      });
    }, 1000);
  };

  const saveMemo = () => {
    dispatch(updateScreenMemo(params.id, memo));
    setMemoSaved(true);
    setTimeout(() => setMemoSaved(false), 2000);
  };

  const handleSaveScreenName = async () => {
    const newName = (screenNameEdit || "").trim();
    if (!newName) return;
    const sync = nameEndsWithM(newName) ? false : nameSyncWithPlaylist;
    const hadPlaylist = !!playlistInfo?.playlist?._id || !!playlistInfo?.playlist;
    setNameSaving(true);
    try {
      const res = await dispatch(updateScreenName(params.id, { name: newName, nameSyncWithPlaylist: sync }));
      if (res?.status === 200 && nameEndsWithM(newName) && hadPlaylist) {
        setPendingNameSave({ name: newName });
        setOpenNameSyncDialog(true);
      }
    } finally {
      setNameSaving(false);
    }
  };

  const handleNameSyncDialogChoice = async (choice) => {
    if (!pendingNameSave) return;
    const { name } = pendingNameSave;
    if (choice === "unassign") {
      await dispatch(updateScreenName(params.id, { name, nameSyncWithPlaylist: false, removeAssignedPlaylist: true }));
    } else if (choice === "delete") {
      await dispatch(updateScreenName(params.id, { name, nameSyncWithPlaylist: false, removeAssignedPlaylist: true, deletePlaylist: true }));
      dispatch(getPlaylist(user?._id));
    }
    setOpenNameSyncDialog(false);
    setPendingNameSave(null);
  };

  const selectPlaylistById = (playlistId) => {
    setCode(playlistId);
    setOpenPlaylistDialog(false);
    setPlaylistSearch("");
    if (isBulkTarget) {
      filteredIds.forEach(id => {
        dispatch(addPlaylistToScreen({ id, value: playlistId }));
        emitAssignPlaylistToScreen(id);
      });
    } else {
      dispatch(addPlaylistToScreen({ id: params.id, value: playlistId }));
      emitAssignPlaylistToScreen(params.id);
    }
  };

  const isOnline = playlistInfo?.status === true;

  // 실시간 통계 우선, 없으면 DB 값 사용
  const ram     = deviceStats?.ram     || playlistInfo?.ram;
  const storage = deviceStats?.storage || playlistInfo?.storage;
  const cpu     = deviceStats?.cpu     || playlistInfo?.cpu;
  const ramTotal  = ram?.total   ? +ram.total.split(" ")[0]   : 0;
  const ramFree   = ram?.free    ? +ram.free.split(" ")[0]    : 0;
  const storTotal = storage?.total ? +storage.total.split(" ")[0] : 0;
  const storFree  = storage?.free  ? +storage.free.split(" ")[0]  : 0;
  const cpuUsage  = cpu?.usage ? parseFloat(cpu.usage) : null;

  const infoRows = [
    { label: 'Code',        value: playlistInfo.code },
    { label: 'Device Name', value: playlistInfo?.device_details?.device_name },
    { label: 'Name',        value: playlistInfo.name },
    { label: 'Manufacturer',value: playlistInfo?.device_details?.manufacturer },
    { label: 'OS',          value: playlistInfo?.device_details?.os_version },
    { label: 'IP Address',  value: playlistInfo?.device_details?.ip },
    { label: 'MAC',         value: playlistInfo.mac },
    { label: 'Resolution',  value: playlistInfo?.device_details?.width ? `${playlistInfo.device_details.width} × ${playlistInfo.device_details.height}` : '-' },
    { label: 'Software',    value: playlistInfo?.device_details?.software },
    { label: 'Root Level',  value: playlistInfo?.device_details?.root_level },
  ];

  return (
    <>
      <Layout title={"Setting"} user={user}>
        <Grid container sx={{ px: 1, pt: 1 }}>

          {/* ── Left panel ── */}
          <Grid item lg={8} xs={12}>
            <Box sx={{ background: "white", p: 2.5, m: "14px 10px", borderRadius: "16px", boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>

              {/* Bulk nav */}
              {filteredIds.length > 0 && (
                <Box sx={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:'8px', pb:1, borderBottom:'1px solid rgba(85,31,255,0.1)', mb:1.5 }}>
                  <span style={{ fontSize:'12px', color:'#888' }}>
                    {currentIndex + 1} / {filteredIds.length}
                  </span>
                  {['이전', '다음'].map((label, i) => {
                    const targetId = i === 0 ? prevId : nextId;
                    return (
                      <button key={label} disabled={!targetId}
                        onClick={() => navigate(`/setting/${targetId}`)}
                        style={{ padding:'6px 14px', borderRadius:'6px', border:'1px solid #551FFF', background: targetId ? '#551FFF' : '#eee', color: targetId ? 'white' : '#aaa', cursor: targetId ? 'pointer' : 'default', fontSize:'13px' }}
                      >
                        {i === 0 ? '◀ ' : ''}{label}{i === 1 ? ' ▶' : ''}
                      </button>
                    );
                  })}
                  <button onClick={() => navigate('/screen')} style={{ padding:'6px 14px', borderRadius:'6px', border:'1px solid #ccc', background:'white', color:'#555', cursor:'pointer', fontSize:'13px' }}>목록</button>
                </Box>
              )}

              {/* Header */}
              <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', pb:1.5, borderBottom:'3px solid #551FFF', mb:2 }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                  <Box sx={{ width:48, height:48, borderRadius:'12px', background:'rgba(85,31,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <img src="/images/MobileScreen.png" alt="" style={{ width:30, height:30, objectFit:'contain' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight:700, fontSize:16, color:'#15192C' }}>
                      {playlistInfo?.device_details?.device_name || playlistInfo.name || '-'}
                    </Typography>
                    <Box sx={{ display:'flex', alignItems:'center', gap:0.8, mt:0.3 }}>
                      <Box sx={{ width:8, height:8, borderRadius:'50%', background: isOnline ? '#22c55e' : '#d1d5db' }} />
                      <Typography sx={{ fontSize:12, color: isOnline ? '#22c55e' : '#9ca3af' }}>
                        {isOnline ? 'Connected' : 'Disconnected'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Playlist selector */}
                <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                  {isBulkTarget && (
                    <Box sx={{ fontSize:'11px', color:'#551FFF', background:'rgba(85,31,255,0.08)', borderRadius:'6px', px:1.5, py:0.5 }}>
                      {filteredIds.length}개 일괄 적용
                    </Box>
                  )}
                  {playlistInfo?.playlist === undefined || playlistInfo?.screenAssigned ? (
                    <button
                      onClick={() => { setOpenPlaylistDialog(true); setPlaylistSearch(""); }}
                      style={{ height:"34px", minWidth:"180px", background:"rgba(217,217,217,0.3)", border:"1px solid rgba(85,31,255,0.3)", borderRadius:"6px", cursor:"pointer", fontSize:"13px", color: code ? "#15192C" : "#888", textAlign:"left", padding:"0 10px" }}
                    >
                      {code ? (playlist?.find(p => p._id === code)?.name || "선택됨") : "플레이리스트 선택..."}
                    </button>
                  ) : (
                    <Box sx={{ display:"flex", gap:"5px", alignItems:"center" }}>
                      <Typography sx={{ fontSize:13, color:'#15192C' }}>{playlistInfo?.playlist?.name}</Typography>
                      <BorderColorIcon style={{ color:"#6179FB", cursor:"pointer", fontSize:16 }} onClick={() => dispatch(uncheckAssignedScreen(playlistInfo._id))} />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* 스크린 이름 편집 + 연동 토글 */}
              <Box sx={{ mb:2, p:2, background:'rgba(85,31,255,0.04)', borderRadius:'10px', border:'1px solid rgba(85,31,255,0.12)' }}>
                <Typography sx={{ fontSize:11, color:'#92959E', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', mb:1 }}>스크린 이름</Typography>
                <Box sx={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:2 }}>
                  <TextField
                    size="small"
                    value={screenNameEdit}
                    onChange={(e) => setScreenNameEdit(e.target.value)}
                    placeholder="스크린 이름"
                    sx={{ minWidth:200, flex:1, '& .MuiInput-root': { fontSize:14 } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={nameSyncWithPlaylist}
                        onChange={(e) => setNameSyncWithPlaylist(e.target.checked)}
                        size="small"
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#551FFF' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#551FFF' } }}
                      />
                    }
                    label={<Typography sx={{ fontSize:12, color:'#555' }}>플레이리스트 이름 연동 (ON: 함께 변경)</Typography>}
                  />
                  <button
                    onClick={handleSaveScreenName}
                    disabled={nameSaving || !(screenNameEdit || '').trim()}
                    style={{ padding:'8px 16px', borderRadius:8, border:'none', background: (screenNameEdit || '').trim() && !nameSaving ? '#551FFF' : '#ccc', color:'white', fontSize:13, fontWeight:600, cursor: (screenNameEdit || '').trim() && !nameSaving ? 'pointer' : 'default' }}
                  >
                    {nameSaving ? '저장 중...' : '이름 저장'}
                  </button>
                </Box>
                {nameEndsWithM(screenNameEdit) && (
                  <Typography sx={{ fontSize:11, color:'#f57f17', mt:1 }}>
                    이름이 -M / _M 으로 끝나 연동이 해제되며, 저장 시 기존 할당 플레이리스트 제거 여부를 선택할 수 있습니다.
                  </Typography>
                )}
              </Box>

              {/* Info grid */}
              <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0, borderRadius:'10px', overflow:'hidden', border:'1px solid rgba(85,31,255,0.12)', mb:2 }}>
                {infoRows.map(({ label, value }, i) => (
                  <Box key={label} sx={{ px:2.5, py:1.5, background: i % 4 < 2 ? 'rgba(85,31,255,0.02)' : 'white', borderBottom: i < infoRows.length - 2 ? '1px solid rgba(85,31,255,0.08)' : 'none', borderRight: i % 2 === 0 ? '1px solid rgba(85,31,255,0.08)' : 'none' }}>
                    <Typography sx={{ fontSize:11, color:'#92959E', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', mb:0.3 }}>{label}</Typography>
                    <Typography sx={{ fontSize:13, color:'#15192C', fontWeight:500 }}>{value || '-'}</Typography>
                  </Box>
                ))}
              </Box>

              {/* 리소스 통계 (RAM / Storage / CPU) */}
              {(ram?.total || storage?.total || cpuUsage !== null) && (
                <Box sx={{ mb:2 }}>
                  {/* 헤더: 제목 + 마지막 업데이트 시각 */}
                  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
                    <Typography sx={{ fontSize:11, color:'#92959E', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>
                      디바이스 리소스
                    </Typography>
                    {deviceStats?.updatedAt && (
                      <Typography sx={{ fontSize:10, color:'#bbb' }}>
                        {new Date(deviceStats.updatedAt).toLocaleTimeString()} 기준
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display:'flex', gap:2, justifyContent:'center', flexWrap:'wrap' }}>
                    {ram?.total && (
                      <DonutChart views="Ram"
                        series={[ramTotal - ramFree, ramFree]}
                        labels={[`Usage ${ram.total.split(" ")[1]}`, `Free ${ram.free.split(" ")[1]}`]}
                      />
                    )}
                    {storage?.total && (
                      <DonutChart views="Storage"
                        series={[storTotal - storFree, storFree]}
                        labels={[`Usage ${storage.total.split(" ")[1]}`, `Free ${storage.free.split(" ")[1]}`]}
                      />
                    )}
                    {cpuUsage !== null && (
                      <DonutChart views="CPU"
                        series={[Math.round(cpuUsage), Math.round(100 - cpuUsage)]}
                        labels={[`사용 ${Math.round(cpuUsage)}%`, `여유 ${Math.round(100 - cpuUsage)}%`]}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Memo */}
              <Box sx={{ borderTop:'1px solid rgba(85,31,255,0.1)', pt:2 }}>
                <Typography sx={{ fontSize:12, color:'#92959E', fontWeight:600, mb:0.8 }}>MEMO</Typography>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  rows={3}
                  style={{ width:"100%", border:"1px solid rgba(85,31,255,0.2)", borderRadius:"8px", padding:"10px", fontSize:"13px", resize:"vertical", fontFamily:"inherit", outline:"none", boxSizing:'border-box' }}
                  placeholder="메모를 입력하세요..."
                />
                <Box sx={{ display:"flex", justifyContent:"flex-end", mt:0.8, gap:1, alignItems:"center" }}>
                  {memoSaved && <span style={{ fontSize:"12px", color:"#551FFF" }}>저장됨</span>}
                  <button onClick={saveMemo} style={{ background:"#551FFF", color:"white", border:"none", borderRadius:"6px", padding:"6px 18px", fontSize:"13px", cursor:"pointer" }}>저장</button>
                </Box>
              </Box>

            </Box>
          </Grid>

          {/* ── Right panel ── */}
          <Grid item lg={4} xs={12}>
            <Box sx={{ background:"white", m:"14px 10px", borderRadius:"16px", boxShadow:"0 4px 20px rgba(0,0,0,0.06)", overflow:'hidden' }}>

              {/* Panel header */}
              <Box sx={{ background:'#551FFF', px:2.5, py:1.5, display:'flex', alignItems:'center', gap:1 }}>
                <CameraAltIcon sx={{ color:'white', fontSize:18 }} />
                <Typography sx={{ color:'white', fontWeight:700, fontSize:14 }}>화면 미리보기</Typography>
              </Box>

              {/* Screenshot area */}
              <Box sx={{ background:'#1a1a2e', minHeight:220, display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
                {screenshotLoading && (
                  <Box sx={{ textAlign:'center', color:'#aaa' }}>
                    <Typography sx={{ fontSize:13 }}>캡처 중...</Typography>
                  </Box>
                )}
                {!screenshotLoading && screenshot && (
                  <img src={`data:image/png;base64,${screenshot}`} alt="screenshot"
                    style={{ maxWidth:'100%', maxHeight:280, objectFit:'contain', display:'block' }} />
                )}
                {!screenshotLoading && !screenshot && (
                  <Box sx={{ textAlign:'center', color:'#555' }}>
                    <CameraAltIcon sx={{ fontSize:40, mb:1 }} />
                    <Typography sx={{ fontSize:12 }}>스크린샷 없음</Typography>
                  </Box>
                )}
              </Box>

              {/* Action buttons */}
              <Box sx={{ p:2, display:'flex', flexDirection:'column', gap:1.2 }}>

                {/* 연결 상태 체크 */}
                <button
                  onClick={handlePingDevice}
                  disabled={pinging}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #22c55e', background:'white', color: pinging ? '#9ca3af' : '#16a34a', cursor: pinging ? 'not-allowed' : 'pointer', fontSize:'13px', fontWeight:600 }}
                >
                  <span style={{ fontSize:14 }}>📡</span>
                  {pinging ? '응답 대기 중...' : '연결 상태 확인'}
                </button>

                {/* Ping 결과 표시 */}
                {pingResult && (
                  <Box sx={{ px:1.5, py:1, borderRadius:'8px', border:'1px solid', borderColor: pingResult.connected ? '#bbf7d0' : '#fecaca', background: pingResult.connected ? '#f0fdf4' : '#fef2f2', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <Box sx={{ width:8, height:8, borderRadius:'50%', background: pingResult.connected ? '#22c55e' : '#ef4444' }} />
                      <Typography sx={{ fontSize:12, fontWeight:600, color: pingResult.connected ? '#16a34a' : '#dc2626' }}>
                        {pingResult.connected ? `응답 완료 (${pingResult.latency}ms)` : '응답 없음 (오프라인)'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize:11, color:'#9ca3af' }}>{pingResult.ts}</Typography>
                  </Box>
                )}

                <button
                  onClick={handleRequestScreenshot}
                  disabled={!isOnline || screenshotLoading}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'none', background: isOnline ? '#551FFF' : '#e5e7eb', color: isOnline ? 'white' : '#9ca3af', cursor: isOnline ? 'pointer' : 'not-allowed', fontSize:'13px', fontWeight:600 }}
                >
                  <CameraAltIcon sx={{ fontSize:16 }} />
                  {screenshotLoading ? '캡처 중...' : '스크린샷 요청'}
                </button>

                <button
                  onClick={() => setOpenModel(true)}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #551FFF', background:'white', color:'#551FFF', cursor:'pointer', fontSize:'13px', fontWeight:600 }}
                >
                  <MailOutlineIcon sx={{ fontSize:16 }} />
                  메시지 전송 (흐르는 자막)
                </button>

                <button
                  onClick={handleClearCache}
                  disabled={!isOnline}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #f59e0b', background:'white', color: isOnline ? '#f59e0b' : '#9ca3af', cursor: isOnline ? 'pointer' : 'not-allowed', fontSize:'13px', fontWeight:600 }}
                >
                  <DeleteSweepIcon sx={{ fontSize:16 }} />
                  캐시 초기화
                </button>

                {/* 리소스 조회 버튼 */}
                <button
                  onClick={handleRequestStats}
                  disabled={!isOnline || statsLoading}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #8b5cf6', background:'white', color: (isOnline && !statsLoading) ? '#8b5cf6' : '#9ca3af', cursor: (isOnline && !statsLoading) ? 'pointer' : 'not-allowed', fontSize:'13px', fontWeight:600 }}
                >
                  <MemoryIcon sx={{ fontSize:16 }} />
                  {statsLoading ? '조회 중...' : '리소스 조회'}
                </button>

                {/* 리소스 최신 수신 시각 표시 */}
                {deviceStats?.updatedAt && !statsLoading && (
                  <Box sx={{ px:1.5, py:0.8, borderRadius:'8px', border:'1px solid #ede9fe', background:'#faf5ff', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <Typography sx={{ fontSize:11, color:'#7c3aed', fontWeight:600 }}>CPU {cpuUsage !== null ? `${Math.round(cpuUsage)}%` : '-'}</Typography>
                    <Typography sx={{ fontSize:10, color:'#aaa' }}>{new Date(deviceStats.updatedAt).toLocaleTimeString()} 기준</Typography>
                  </Box>
                )}
              </Box>

              {/* Connected playlist info */}
              {playlistInfo?.playlist?.name && (
                <Box sx={{ mx:2, mb:2, p:1.5, background:'rgba(85,31,255,0.04)', borderRadius:'8px', border:'1px solid rgba(85,31,255,0.12)' }}>
                  <Typography sx={{ fontSize:11, color:'#92959E', fontWeight:600, mb:0.3 }}>현재 플레이리스트</Typography>
                  <Typography sx={{ fontSize:13, color:'#551FFF', fontWeight:600 }}>🎬 {playlistInfo.playlist.name}</Typography>
                </Box>
              )}
            </Box>
          </Grid>

        </Grid>
      </Layout>

      {/* Send Message dialog */}
      <Dialog fullWidth open={openModel} onClose={() => setOpenModel(false)}>
        <DialogTitle style={{ textAlign:"center", borderBottom:"2px solid #551fff", fontSize:"18px", fontWeight:700 }}>
          메시지 전송 (흐르는 자막)
        </DialogTitle>
        <DialogContent>
          <div className="screen-form">
            <div className="icon-screen">
              <MailOutlineIcon sx={{ color:"#551fff", height:"50px", width:"50px" }} />
            </div>
            <Box sx={{ mb:1.5, px:0.5, py:1, background:'rgba(85,31,255,0.04)', borderRadius:'8px', border:'1px solid rgba(85,31,255,0.12)' }}>
              <Typography sx={{ fontSize:12, color:'#551FFF', textAlign:'center' }}>
                📺 앱 하단에 흐르는 자막(marquee)으로 표시됩니다
              </Typography>
              <Typography sx={{ fontSize:11, color:'#92959E', textAlign:'center', mt:0.3 }}>
                소켓 이벤트: <code>message_recieved</code> → tickerText
              </Typography>
            </Box>
            <div className="form-fields">
              <TextField type="text" value={message} onChange={(e) => setMessage(e.target.value)} variant="outlined" label="메시지 내용" multiline fullWidth placeholder="화면 하단에 표시할 메시지를 입력하세요" />
            </div>
            <div className="form-fields" style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
              <TextField
                type="number" value={duration}
                onChange={(e) => setDuration(e.target.value)}
                label="표시 시간(초)"
                helperText="앱에서 ticker 표시 시간"
                sx={{ width:"160px" }}
                inputProps={{ min:1, max:300 }}
              />
            </div>
            <Box sx={{ display:"flex", mt:4, mb:3, justifyContent:"center", gap:2 }}>
              <button style={{ background:"white", padding:"8px", width:"130px", color:"#551FFF", border:"1px solid #551FFF", borderRadius:"5px", cursor:"pointer" }} onClick={() => setOpenModel(false)}>취소</button>
              <button style={{ background: message.trim() ? "#551FFF" : "#ccc", padding:"8px", width:"130px", color:"white", border:"none", borderRadius:"5px", cursor: message.trim() ? "pointer" : "default" }} onClick={submit} disabled={!message.trim()}>전송</button>
            </Box>
          </div>
        </DialogContent>
      </Dialog>

      {/* Playlist select dialog */}
      <Dialog fullWidth open={openPlaylistDialog} onClose={() => { setOpenPlaylistDialog(false); setPlaylistSearch(""); }}>
        <DialogTitle style={{ textAlign:'center', borderBottom:'2px solid #551fff', fontSize:'18px', fontWeight:700 }}>
          플레이리스트 선택
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py:2, display:'flex', flexDirection:'column', gap:1.5 }}>
            {isBulkTarget && (
              <p style={{ fontSize:'12px', color:'#551FFF', textAlign:'center', margin:0 }}>
                필터된 {filteredIds.length}개 스크린에 일괄 적용됩니다.
              </p>
            )}
            <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
              <input
                value={playlistSearch}
                onChange={e => setPlaylistSearch(e.target.value)}
                placeholder="이름 검색..."
                style={{ flex:1, height:'36px', border:'1px solid rgba(85,31,255,0.3)', borderRadius:'8px', padding:'0 10px', fontSize:'13px', outline:'none' }}
              />
              <select
                value={playlistSort}
                onChange={e => setPlaylistSort(e.target.value)}
                style={{ height:'36px', border:'1px solid rgba(85,31,255,0.3)', borderRadius:'8px', padding:'0 8px', fontSize:'12px', color:'#555' }}
              >
                <option value="name-asc">이름 A→Z</option>
                <option value="name-desc">이름 Z→A</option>
                <option value="date-desc">최신순</option>
                <option value="date-asc">오래된순</option>
              </select>
            </Box>
            <Box sx={{ maxHeight:'300px', overflowY:'auto', border:'1px solid rgba(85,31,255,0.15)', borderRadius:'8px' }}>
              {(() => {
                const searched = (playlist || []).filter(p => p.name?.toLowerCase().includes(playlistSearch.toLowerCase()));
                const sorted = [...searched].sort((a, b) => {
                  if (playlistSort === 'name-asc') return (a.name || '').localeCompare(b.name || '');
                  if (playlistSort === 'name-desc') return (b.name || '').localeCompare(a.name || '');
                  if (playlistSort === 'date-desc') return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
                  return new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0);
                });
                if (sorted.length === 0) return (
                  <p style={{ textAlign:'center', color:'#aaa', fontSize:'13px', padding:'20px' }}>결과 없음</p>
                );
                return sorted.map(p => (
                  <Box key={p._id} onClick={() => selectPlaylistById(p._id)}
                    sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1.5, cursor:'pointer', borderBottom:'1px solid rgba(0,0,0,0.05)', background: code === p._id ? 'rgba(85,31,255,0.08)' : 'white', '&:hover': { background:'rgba(85,31,255,0.05)' } }}
                  >
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <Box sx={{ width:10, height:10, borderRadius:'50%', border:'2px solid #551FFF', background: code === p._id ? '#551FFF' : 'white', flexShrink:0 }} />
                      <span style={{ fontSize:'14px', color:'#15192C' }}>{p.name}</span>
                    </Box>
                    <span style={{ fontSize:'11px', color:'#aaa' }}>{p.content?.length || 0}개 콘텐츠</span>
                  </Box>
                ));
              })()}
            </Box>
            <Box sx={{ display:'flex', justifyContent:'center', mt:1 }}>
              <button onClick={() => { setOpenPlaylistDialog(false); setPlaylistSearch(""); }} style={{ background:'white', padding:'8px', width:'130px', color:'#551FFF', border:'1px solid #551FFF', borderRadius:'5px', cursor:'pointer' }}>닫기</button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* -M/_M 저장 시: 기존 할당 플레이리스트 제거 여부 */}
      <Dialog open={openNameSyncDialog} onClose={() => { setOpenNameSyncDialog(false); setPendingNameSave(null); }}>
        <DialogTitle sx={{ fontWeight:700, color:'#551FFF' }}>플레이리스트 할당 해제</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize:13, color:'#555' }}>
            스크린 이름이 -M/_M으로 끝나 연동이 해제되었습니다. 기존에 할당된 플레이리스트를 어떻게 할까요?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2, flexWrap:'wrap', gap:1 }}>
          <Button onClick={() => handleNameSyncDialogChoice('unassign')} variant="outlined" sx={{ borderColor:'#551FFF', color:'#551FFF' }}>할당만 해제</Button>
          <Button onClick={() => handleNameSyncDialogChoice('delete')} variant="contained" color="error">할당 해제 후 플레이리스트 삭제</Button>
          <Button onClick={() => { setOpenNameSyncDialog(false); setPendingNameSave(null); }} sx={{ color:'#888' }}>유지</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Setting;
